/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useMemo } from 'react';

import type { PromoTemplateFaqsFragment } from 'bundles/front-page/__generated__/contentfulTypes';
import FAQsModule from 'bundles/front-page/redesign-modules/faq/FaqModule';
import Markdown from 'bundles/front-page/redesign-modules/faq/Markdown';
import FaqsSchemaMarkup from 'bundles/seo/components/FaqsSchemaMarkup';

type Props = {
  content: PromoTemplateFaqsFragment;
};

const styles = {
  markdown: css`
    // we want to display unordered lists as bullets, so we need to
    // override the styles defined in sharedMarkdownStyles
    ul {
      padding-left: var(--cds-spacing-400);

      li {
        display: list-item;
        list-style-type: disc;
        padding-left: var(--cds-spacing-100);
      }
    }
  `,
};

const FaqModuleRenderer = ({ content }: Props) => {
  const faqs = useMemo(
    () =>
      content.faqsCollection?.items
        .map((faq) => {
          if (!faq || !faq.question || !faq.answer) return false;

          return {
            question: faq.question,
            plainTextAnswer: faq.answer,
            answer: <Markdown source={faq.answer} css={styles.markdown} />,
          };
        })
        .filter(Boolean),
    [content]
  );

  if (!faqs || !faqs.length) {
    return null;
  }

  return (
    <>
      <FAQsModule faqs={faqs} headline={content.headline} />
      <FaqsSchemaMarkup faqs={faqs.map(({ question, plainTextAnswer }) => ({ question, answer: plainTextAnswer }))} />
    </>
  );
};

export default FaqModuleRenderer;
